import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';
import { Order, OrderStatus } from '../types';

const STATUS_FILTER: { value: string; label: string }[] = [
  { value: '', label: 'All Completed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'completed', label: 'Completed' },
];

export default function CompletedOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [qbFilter, setQbFilter] = useState<'all' | 'pending' | 'done'>('all');

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const mapOrder = (order: any) => ({
    ...order,
    dealerName: order.dealerName || order.companyName || order.company_name || 'Unknown Dealer',
    total: order.total || order.totalAmount || order.total_amount || 0,
    orderNumber: order.orderNumber || order.order_number,
    createdAt: order.createdAt || order.created_at,
    updatedAt: order.updatedAt || order.updated_at,
    shippedAt: order.shippedAt || order.shipped_at,
    deliveredAt: order.deliveredAt || order.delivered_at,
    trackingNumber: order.trackingNumber || order.tracking_number,
    sentToQuickbooks: order.sentToQuickbooks || order.sent_to_quickbooks || false,
    sentToQuickbooksAt: order.sentToQuickbooksAt || order.sent_to_quickbooks_at || null,
  });

  const loadOrders = async () => {
    try {
      setLoading(true);
      if (statusFilter) {
        const res = await api.orders.getOrders({ status: statusFilter as OrderStatus, limit: 100, includeArchived: true });
        setOrders((res.data || res.items || []).map(mapOrder));
      } else {
        // Load shipped, delivered, and completed
        const [shippedRes, deliveredRes, completedRes] = await Promise.all([
          api.orders.getOrders({ status: 'shipped' as OrderStatus, limit: 100, includeArchived: true }),
          api.orders.getOrders({ status: 'delivered' as OrderStatus, limit: 100, includeArchived: true }),
          api.orders.getOrders({ status: 'completed' as OrderStatus, limit: 100, includeArchived: true }),
        ]);
        const shipped = (shippedRes.data || shippedRes.items || []).map(mapOrder);
        const delivered = (deliveredRes.data || deliveredRes.items || []).map(mapOrder);
        const completed = (completedRes.data || completedRes.items || []).map(mapOrder);
        const all = [...shipped, ...delivered, ...completed].sort((a: any, b: any) => {
          const dateA = a.shippedAt || a.deliveredAt || a.updatedAt;
          const dateB = b.shippedAt || b.deliveredAt || b.updatedAt;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
        setOrders(all);
      }
    } catch (error) {
      console.error('Failed to load completed orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDelivered = async (orderId: string) => {
    try {
      setActionLoading(orderId);
      await api.orders.updateOrderStatus(orderId, 'delivered' as OrderStatus);
      await loadOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleQuickbooks = async (orderId: string, currentState: boolean) => {
    try {
      setActionLoading(orderId);
      await api.orders.markSentToQuickbooks(orderId, !currentState);
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, sentToQuickbooks: !currentState, sentToQuickbooksAt: !currentState ? new Date().toISOString() : null } as any : o
      ));
    } catch (error) {
      console.error('Failed to update QuickBooks status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchQuery ||
      order.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.dealerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.sidemark?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order as any).trackingNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    if (qbFilter === 'pending') return !(order as any).sentToQuickbooks;
    if (qbFilter === 'done') return (order as any).sentToQuickbooks;
    return true;
  });

  const shippedCount = orders.filter(o => o.status === 'shipped').length;
  const deliveredCount = orders.filter(o => o.status === 'delivered').length;
  const needsQBCount = orders.filter(o => !(o as any).sentToQuickbooks).length;

  return (
    <div>
      <PageHeader
        title="Completed Orders"
        subtitle="Shipped and delivered orders"
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
          <p className="text-sm text-gray-500">Total</p>
        </Card>
        <Card className="text-center border-l-4 border-purple-400">
          <p className="text-2xl font-bold text-purple-600">{shippedCount}</p>
          <p className="text-sm text-gray-500">Shipped</p>
        </Card>
        <Card className="text-center border-l-4 border-emerald-400">
          <p className="text-2xl font-bold text-emerald-600">{deliveredCount}</p>
          <p className="text-sm text-gray-500">Delivered</p>
        </Card>
        <div
          onClick={() => setQbFilter(qbFilter === 'pending' ? 'all' : 'pending')}
          className={`bg-white rounded-xl border-2 p-6 text-center cursor-pointer transition-colors border-l-4 ${needsQBCount > 0 ? 'border-red-500 bg-red-50 hover:bg-red-100' : 'border-green-400 bg-green-50 hover:bg-green-100'}`}
        >
          <p className={`text-2xl font-bold ${needsQBCount > 0 ? 'text-red-600' : 'text-green-600'}`}>{needsQBCount}</p>
          <p className={`text-sm font-medium ${needsQBCount > 0 ? 'text-red-500' : 'text-green-500'}`}>Needs QuickBooks</p>
        </div>
      </div>

      {/* QB Alert Banner */}
      {needsQBCount > 0 && qbFilter !== 'pending' && (
        <div
          className="mb-4 bg-red-50 border-2 border-red-300 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors"
          onClick={() => setQbFilter('pending')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
              {needsQBCount}
            </div>
            <div>
              <p className="font-bold text-red-800 text-lg">Invoices Need QuickBooks Entry</p>
              <p className="text-red-600 text-sm">{needsQBCount} order{needsQBCount !== 1 ? 's' : ''} not yet entered into QuickBooks</p>
            </div>
          </div>
          <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by order, dealer, sidemark, or tracking..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {STATUS_FILTER.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setQbFilter('all')}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${qbFilter === 'all' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
            >
              All
            </button>
            <button
              onClick={() => setQbFilter('pending')}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${qbFilter === 'pending' ? 'bg-red-600 text-white shadow' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Needs QB
            </button>
            <button
              onClick={() => setQbFilter('done')}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${qbFilter === 'done' ? 'bg-green-600 text-white shadow' : 'text-gray-500 hover:text-gray-700'}`}
            >
              QB Done
            </button>
          </div>
        </div>
      </Card>

      {/* Orders Table */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <p className="text-lg font-medium">No completed orders</p>
            <p className="text-sm mt-1">Orders will appear here after they ship</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[60px]">QB</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sidemark</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipped</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order: any) => {
                  const needsQB = !order.sentToQuickbooks;
                  return (
                    <tr key={order.id} className={`hover:bg-gray-50 ${needsQB ? 'border-l-4 border-l-red-500' : ''}`}>
                      {/* QuickBooks status */}
                      <td className="px-3 py-3 text-center">
                        <button
                          onClick={() => handleToggleQuickbooks(order.id, order.sentToQuickbooks)}
                          disabled={actionLoading === order.id}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            order.sentToQuickbooks
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-red-100 text-red-600 hover:bg-red-200 ring-2 ring-red-300 animate-pulse'
                          } disabled:opacity-50`}
                          title={order.sentToQuickbooks
                            ? `Sent to QB ${order.sentToQuickbooksAt ? format(new Date(order.sentToQuickbooksAt), 'MMM d, yyyy') : ''} — click to undo`
                            : 'NOT in QuickBooks — click to mark as entered'
                          }
                        >
                          {order.sentToQuickbooks ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link to={`/orders/${order.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{order.dealerName}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{order.sidemark || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <StatusBadge status={order.status} type="order" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${Number(order.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.shippedAt ? format(new Date(order.shippedAt), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {order.trackingNumber ? (
                          <span className="text-blue-600 font-mono text-xs">{order.trackingNumber}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <Link to={`/orders/${order.id}`} className="text-gray-600 hover:text-gray-900">View</Link>
                          {order.status === 'shipped' && (
                            <button
                              onClick={() => handleMarkDelivered(order.id)}
                              disabled={actionLoading === order.id}
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading === order.id ? '...' : 'Mark Delivered'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
